import {
  ButtonComponentActionResultTypenames,
  Maybe,
  SelfFundedItemResultTypenames,
  SelfFundedOfferTile,
  TileStatus,
} from '@varo/graphql-schema-types';
import { HomeCardDependencies } from 'features/home/HomeScreen/dependencies/types';
import { HomeActivatedOfferCardProps } from 'features/rewards/components/HomeActivatedOfferCard/HomeActivatedOfferCard';
import { HomeOfferCardProps } from 'features/rewards/components/HomeOfferCard/HomeOfferCard';
import { IconKeyType } from 'shared/components/vxl/icon/Icon/Icon';
import { VXLComponentId } from 'shared/renderer/types';
import { tagColorMapper } from 'shared/utils/tagColorMapper/tagColorMapper';

interface SelfFundedOfferTileNode {
  id: string;
  name:
    | VXLComponentId.HOME_OFFER_CARD
    | VXLComponentId.HOME_ACTIVATED_OFFER_CARD;
  props: HomeOfferCardProps | HomeActivatedOfferCardProps;
}

const selfFundedOfferTile = (
  tile: SelfFundedOfferTile,
  dependencies: HomeCardDependencies,
): Maybe<SelfFundedOfferTileNode> => {
  if (
    tile.status !== TileStatus.Loaded ||
    !tile.selfFundedOfferCard ||
    typeof tile.selfFundedOfferCard !== 'object' ||
    !('__typename' in tile.selfFundedOfferCard)
  ) {
    return null;
  }

  if (
    tile.selfFundedOfferCard &&
    tile.selfFundedOfferCard.__typename ===
      SelfFundedItemResultTypenames.PromotionalSelfFundedOffer
  ) {
    const promotionalOffer = tile.selfFundedOfferCard;
    const { id, iconUrls, title, tag, footerButton } = promotionalOffer;

    const bodyItems = iconUrls.map((iconUrl, index) => ({
      id: `${id}-icon-${index}`,
      iconUrl,
      title: { text: title || '' },
    }));

    const navigationAction = footerButton?.buttonOnClick;

    const handleNavigation =
      navigationAction?.__typename ===
      ButtonComponentActionResultTypenames.RouteNavigationAction
        ? () => dependencies.navigateToRoute(navigationAction)
        : () => {};

    const offerCardProps: HomeOfferCardProps = {
      id: id,
      title: { text: title },
      bodyItems,
      buttonTitle: footerButton?.buttonText
        ? { text: footerButton.buttonText }
        : undefined,
      tag: tag
        ? {
            text: tag.text,
            type: tagColorMapper(tag.tagColor),
            iconKey: tag.icon as IconKeyType | undefined,
          }
        : undefined,
      onPress: handleNavigation,
      onActivate: handleNavigation,
    };
    return {
      id: tile.id,
      name: VXLComponentId.HOME_OFFER_CARD,
      props: offerCardProps,
    };
  } else if (
    tile.selfFundedOfferCard.__typename ===
    SelfFundedItemResultTypenames.ActivatedSelfFundedOffer
  ) {
    const activatedOffer = tile.selfFundedOfferCard;
    const { offerId, avatarUrl, title, subTitle, tag, onClick } =
      activatedOffer;
    const activatedOfferCardProps: HomeActivatedOfferCardProps = {
      offerId,
      avatarUrl,
      title: title,
      subTitle: subTitle,
      tag: tag
        ? {
            text: tag.text,
            type: tagColorMapper(tag.tagColor),
            iconKey: tag.icon as IconKeyType | undefined,
          }
        : undefined,
      onClick: (): void => {
        if (onClick) {
          dependencies.navigateToRoute({
            __typename: 'RouteNavigationAction',
            route: onClick.route,
            analyticsEvent: onClick.analyticsEvent,
          });
        }
      },
      testID: `activated-offer-${offerId}`,
    };
    return {
      id: tile.id,
      name: VXLComponentId.HOME_ACTIVATED_OFFER_CARD,
      props: activatedOfferCardProps,
    };
  }
  return null;
};

export default selfFundedOfferTile;
